// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The single type-discipline unit for rendering an IR ProseSlot (LLM/human narrative).
//
// The load-bearing rule: the prose `text` is rendered AS TEXT and is never parsed for
// numbers. Every figure the reader can trust is resolved from `cell_refs` — dotted IR paths
// pointing at real Cells — and rendered through <CellValue/> beneath the prose. A ProseSlot
// carries no `.value` (enforced by the `ProseSlot` type), so a number can never enter via
// the narrative path; it can only be shown by resolving a ref to a governed Cell.

import { cn } from "@/lib/utils";
import { CheckCircle2, CircleAlert, User, Bot } from "lucide-react";
import { isCell, resolveIrPath, type ProseSlot, type Cell, type IR } from "@/types/ir";
import { CellValue } from "./CellValue";

export function ProseSlotBlock({
  slot,
  ir,
  showRefs = true,
  className,
}: {
  slot: ProseSlot | undefined;
  ir: IR | undefined;
  showRefs?: boolean;
  className?: string;
}) {
  if (!slot) return null;
  const review = slot.review;
  const needsReview = review?.needs_review;

  // Resolve each cell_ref to a real Cell (or note it as unresolved) — numbers come from HERE.
  const refs = (slot.cell_refs ?? []).map((path) => {
    const resolved = resolveIrPath(ir, path);
    return { path, cell: isCell(resolved) ? (resolved as Cell) : undefined };
  });

  return (
    <div
      className={cn(
        "rounded-lg border p-4 space-y-3",
        needsReview ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card",
        className,
      )}
    >
      {/* The prose — rendered as text only, never mined for figures. */}
      <p className="font-body text-sm text-foreground leading-relaxed">{slot.text}</p>

      {/* Interpolated figures — every number the reader can trust, resolved from cell_refs. */}
      {showRefs && refs.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2">
          {refs.map(({ path, cell }) => (
            <span key={path} className="inline-flex items-center gap-1.5 font-body text-[11px]">
              <span className="text-muted-foreground/70">{shortRef(path)}</span>
              {cell ? (
                <CellValue cell={cell} signed={/excess|drift|diff|gap|contribution/i.test(path)} />
              ) : (
                <span className="text-muted-foreground italic">unresolved ref</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Review + adversarial-verify status. */}
      <div className="flex flex-wrap items-center gap-3 font-body text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {slot.author === "human" ? <User className="h-3 w-3" aria-hidden /> : <Bot className="h-3 w-3" aria-hidden />}
          {slot.author === "human" ? "Human-authored" : `LLM${slot.model ? ` · ${slot.model}` : ""}`}
        </span>
        {needsReview ? (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <CircleAlert className="h-3 w-3" aria-hidden /> Needs review
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-primary">
            <CheckCircle2 className="h-3 w-3" aria-hidden /> Cleared
          </span>
        )}
        {review?.adversarial_verify?.attempted && (
          <span title={review.adversarial_verify.note || undefined}>
            skeptic pass: {review.adversarial_verify.refuted ? "REFUTED" : "no contradiction"}
          </span>
        )}
        {review?.holdings_verified && <span>holdings-verified</span>}
      </div>
    </div>
  );
}

// "data.fund_returns.pairs[Japan].active_q2" -> "…pairs[Japan].active_q2"
function shortRef(path: string): string {
  const parts = path.split(".");
  return parts.length <= 2 ? path : "…" + parts.slice(-2).join(".");
}
